import { api } from "@/services/api";

export interface VisitorLabReport {
  id: number;
  testOrderId: number;
  testTypeId: number;
  testName: string;
  clientId: number;
  clientName: string;
  status: 'NOT_STARTED' | 'WAITING_FOR_RESULT' | 'READY_TO_REVIEW' | 'CANCELED';
  resultDocumentUrl?: string;
  otherDocuments?: string[];
  notes?: string;
  uploadedBy?: number;
  uploadedAt?: string;
  reviewedBy?: number;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VisitorLabReportRequest {
  testOrderId: number;
  testTypeId: number;
  testName: string;
  clientId: number;
  clientName: string;
  status: string;
  resultDocumentUrl?: string;
  otherDocuments?: string[];
  notes?: string;
  uploadedBy?: number;
}

export const visitorLabReportApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Super Admin endpoints
    getAllVisitorLabReports: builder.query<VisitorLabReport[], { clientId?: number } | void>({
      query: (params) => ({
        url: "/super-admin/visitor-lab-reports",
        params: {
          clientId: params?.clientId
        }
      }),
      providesTags: ["VisitorLabReports"],
    }),

    getVisitorLabReportById: builder.query<VisitorLabReport, number>({
      query: (id) => `/super-admin/visitor-lab-reports/${id}`,
      providesTags: ["VisitorLabReports"],
    }),

    createVisitorLabReport: builder.mutation<VisitorLabReport, VisitorLabReportRequest>({
      query: (body) => ({
        url: "/super-admin/visitor-lab-reports",
        method: "POST",
        body,
      }),
      invalidatesTags: ["VisitorLabReports"],
    }),

    updateVisitorLabReport: builder.mutation<VisitorLabReport, { id: number; data: VisitorLabReportRequest }>({
      query: ({ id, data }) => ({
        url: `/super-admin/visitor-lab-reports/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["VisitorLabReports"],
    }),

    deleteVisitorLabReport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/super-admin/visitor-lab-reports/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VisitorLabReports"],
    }),

    getVisitorLabReportsByStatus: builder.query<VisitorLabReport[], string>({
      query: (status) => `/super-admin/visitor-lab-reports/status?status=${status}`,
      providesTags: ["VisitorLabReports"],
    }),

    // Client endpoints
    getClientVisitorLabReports: builder.query<VisitorLabReport[], number>({
      query: (clientId) => `/clients/${clientId}/visitor-lab-reports`,
      providesTags: ["VisitorLabReports"],
    }),

    getClientVisitorLabReportsByStatus: builder.query<VisitorLabReport[], { clientId: number; status: string }>({
      query: ({ clientId, status }) => `/clients/${clientId}/visitor-lab-reports/status?status=${status}`,
      providesTags: ["VisitorLabReports"],
    }),

    // General endpoints
    getVisitorLabReportsByTestOrder: builder.query<VisitorLabReport[], number>({
      query: (testOrderId) => `/visitor-lab-reports/order/${testOrderId}`,
      providesTags: ["VisitorLabReports"],
    }),
  }),
});

export const {
  useGetAllVisitorLabReportsQuery,
  useGetVisitorLabReportByIdQuery,
  useCreateVisitorLabReportMutation,
  useUpdateVisitorLabReportMutation,
  useDeleteVisitorLabReportMutation,
  useGetVisitorLabReportsByStatusQuery,
  useGetClientVisitorLabReportsQuery,
  useGetClientVisitorLabReportsByStatusQuery,
  useGetVisitorLabReportsByTestOrderQuery,
} = visitorLabReportApi;